export const basketMetaUpdateKeys = {
  COMMIT: 'commit',
  OFFSET: 'offset',
  REDUCE_PAGE_UNLOCKED: 'reducePageUnlocked',
  STARTED: 'started',
  SIGN_OWNER: 'signOwner',
  SIGN_PRIMARY: 'signPrimary',
  SIGN_SECONDARY: 'signSecondary',
  ASPIRATION_TARGET_SAVED: 'aspirationPercentageTargetAndSaved',
  UNDO_LOCK: 'undo_lock',
  PROJECTIONS: 'projections',
  REDUCTIONS: 'reductions',
  CUSTOM_INITIATIVE: 'customInitiative',
  PATCH_EXISTING_INITIATIVE_ANSWER: 'patchExistingInitiativeAnswer',
  REMOVE_EXISTING_INITIATIVE_ANSWER: 'removeExistingInitiativeAnswer',

  PATCH_INITIATIVE_REQUEST: 'patchInitiativeRequest',
  REMOVE_INITIATIVE_REQUEST: 'removeInitiativeRequest',
  ACKNOWLEDGE_INITIATIVE_REQUEST_INFO: 'acknowledgeInitiativeRequestInfo',

  INITIATIVE_TIP_DISMISSED: 'has_initiative_tip_dismissed',
  TARGETS_BANNER_DISMISSED: 'has_targets_banner_dismissed',
  PACKAGE2_CERT: 'electCerts',
  PACKAGE2_LOCATION: 'electLocations',
  CVS_CONNECTION_REQUEST: 'cvs_connection_requested',
};

export const applicationStates = {
  PENDING_ANNUAL_UPDATE: 'pending_annual_update',
  DRAFT: 'draft',
  COMPLETED: 'completed',
  SIGNED: 'signed',
  SUBMITTED: 'submitted',
  REVIEWED: 'reviewed',
  OFFERDECLARED: 'offerdeclared',
  APPROVED: 'approved',
  CONFIRMED: 'confirmed',
};

// Also defined urlApplicationRoot within API for the getInvited for application url links
//  we define it to 'application' as a fallback to ensure any builds from prior to this continue to work in the same way (with the same urls)
export const urlApplicationRoot = import.meta.env.URL_APPLICATION_ROOT || 'application';

export const dashboardTabs = { ...applicationStates, VIEW_ALL: 'view all', ARCHIVED: 'archived' };

export const debugApplicationStates = {
  DRAFT: 'draft',
  COMPLETED: 'completed',
  SIGNED_COSIGNER_ONLY: 'signed-cosigner-only',
  SIGNED_COSIGNER_AND_LEAD_SIGNER: 'signed-cosigner-and-lead-signer',
  SUBMITTED: 'submitted',
  REVIEWED: 'reviewed',
  OFFERDECLARED: 'offerdeclared',
  APPROVED: 'approved',
  CONFIRMED: 'confirmed',
};

export const groupedDashboardTabs = {
  [applicationStates.DRAFT]: [applicationStates.DRAFT, applicationStates.COMPLETED, applicationStates.CONFIRMED],
  [applicationStates.SIGNED]: [applicationStates.SIGNED],
  [applicationStates.SUBMITTED]: [
    applicationStates.SUBMITTED,
    applicationStates.REVIEWED,
    applicationStates.OFFERDECLARED,
    applicationStates.PENDING_ANNUAL_UPDATE,
  ],
  [applicationStates.APPROVED]: [applicationStates.APPROVED],
};

export const headerStates = {
  APPLICATION: 'application',
  DASHBOARD: 'dashboard',
  SETTINGS: 'settings',
  STATIC: 'static',
};

export const userNotificationTypes = {
  QUOTE_UPDATED: 'quote-updated',
};

export const userRoles = {
  REVIEWER: 'Reviewer',
  APPROVER: 'Approver',
  OWNER: 'Owner',
  SIGNER_PRIMARY: 'Signer primary',
  SIGNER_SECONDARY: 'Signer secondary',
  OBSERVER: 'Observer',
};

export const globalUserRoles = {
  ADMIN: 'Admin',
  APPLICATION_SUPPORT: 'Application-support',
  REPORTING: 'Reporting',
  SUPER_REPORTING: 'Super-reporting',
};

export const associatedUserStates = {
  ACCEPTED: 'accepted',
  PENDING: 'invited',
  DECLINED: 'declined',
  FAILED: 'failed',
  SAVED: 'saved',
};

export const applicationInviteStates = {
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
} as const;

export type ApplicationInviteStatus = (typeof applicationInviteStates)[keyof typeof applicationInviteStates];

export const userPermissions = {
  CREATE_APPLICATION: 'Create application',
  COMPLETE_APPLICATION: 'Complete application',
  SIGN_APPLICATION: 'Sign application',
  SUBMIT_APPLICATION: 'Submit application',
  REJECT_APPLICATION: 'Reject application',
  APPROVE_APPLICATION: 'Approve application',
  REVIEW_APPLICATION: 'Review application',
  DECLARE_OFFER_APPLICATION: 'Declare offer application',
  CONFIRM_APPLICATION: 'Confirm application',
  VIEW_APPLICATION: 'View application',
  VIEW_APPLICATION_SETTINGS: 'View application settings',
  UPDATE_APPLICATION_DETAILS: 'Update application details',
  EDIT_APPLICATION_CURRENCY: 'Can edit application currency',
  ASSIGN_APPLICATION_SUPPORT: 'Assign application support',
  ASSIGN_OWNER: 'Assign owner',
  ASSIGN_REVIEWER: 'Assign reviewer',
  ASSIGN_REVIEWER_WHEN_EMPTY: 'Assign reviewer when empty',
  ASSIGN_APPROVER: 'Assign approver',
  ASSIGN_APPROVER_WHEN_EMPTY: 'Assign approver when empty',
  ASSIGN_SIGNERS: 'Assign signers',
  ASSIGN_OBSERVERS: 'Assign observers',
  CAN_EDIT_APPLICATION_ITEMS: 'Can edit application items',
  CAN_EDIT_APPLICATION_REDUCTION_ITEMS: 'Can edit reduction application items',
  VIEW_APPLICATION_STATE_SELECTOR: 'Can view application state selector',
  VIEW_REPORTING_DASHBOARD: 'View reporting suite',
  VIEW_REPORTING_RESTRICTED_REPORTS: 'View restricted reports',
  VIEW_REPORTING_UNRESTRICTED_REPORTS: 'View unrestricted reports',
  CAN_VIEW_OFFER_SETTING: 'Can view payment discount screen',
  CAN_PAY: 'User allows to pay',
  CAN_ACKNOWLEDGE_REQUEST_INFORMATION: 'Can acknowledge request information',
  CAN_ENABLE_ANNUAL_UPDATE: 'Can enable annual update',
  CAN_DISMISS_ANNUAL_UPDATE: 'Can dismiss annual update',
};

export const applicationEndDateGraceYearDowndownOffset = 0;

const validBpEmailAddressHosts = [
  '@ao.bp.com',
  '@aral.com',
  '@aral.lu',
  '@at.bp.com',
  '@az1.bp.com',
  '@bp.com',
  '@castrol.com',
  '@castrol.com.au',
  '@ch.bp.com',
  '@de.bp.com',
  '@ec1.bp.com',
  '@eu1.bp.com',
  '@fr.bp.com',
  '@me1.bp.com',
  '@mz.bp.com',
  '@no.bp.com',
  '@se1.bp.com',
  '@uk.bp.com',
  '@za.bp.com',
  '@erm.com',
  '@six.agency',
  '@bp365.bp.com',
];

if (import.meta.env.permissive_email === 'true') {
  validBpEmailAddressHosts.push('@gmail.com');
  validBpEmailAddressHosts.push('@hotmail.com');
  validBpEmailAddressHosts.push('@hotmail.co.uk');
}

export { validBpEmailAddressHosts };

export const SAFETY_FACTOR = 15;

export const dashboardButtonLayouts = {
  CTA_SHOW_CONFIRM_OFFER_BTN: 'show-offer-declare-btn',
  CTA_SHOW_APPROVE_BTN: 'show-approve-btn',
  CTA_SHOW_PAY_BTN: 'show-pay-btn',
  CTA_SHOW_OVERRIDE_PAY_BTN: 'show-pay-override',
};

export const contributionTypes = {
  FIXED_AMOUNT: 'fixed-amount',
  PERCENTAGE: 'percentage',
} as const;

export type ContributionType = (typeof contributionTypes)[keyof typeof contributionTypes];

export const emissionCalculationTypes = {
  RENEWABLE: 'renewable',
  NONRENEWABLE: 'nonrenewable',
};

export const renewableAmounts = {
  NONE: 'renewable-none',
  SOME: 'renewable-some',
  ALL: 'renewable-all',
};

export const offsetCategories = {
  NO_OFFSET: 'no-offset',
  SOME_OFFSET: 'some-offset',
  ALL_OFFSET: 'all-offset',
};

export const offsetOverrideCategories = {
  NO_OFFSET: 'no-offset',
  UNLOCK: 'unlock',
};

export const questionScopeCategories = {
  BUILDING_EMISSIONS: 'building-emissions',
  TRAVEL_EMISSIONS: 'travel-emissions',
  SCOPE_1: 'scope1',
  SCOPE_2: 'scope2',
  SCOPE_3: 'scope3',
};

export const basketTableTotalTypes = {
  COUNT: 'count',
  TONNAGE: 'tonnage',
};

export const intensityCategories = {
  FLOOR_SPACE: 'floor-space',
  DISTANCE_TRAVELLED: 'distance-travelled',
  TRAFFIC_UNITS: 'traffic-units',
  FINANCIAL: 'financial',
  FREIGHT_TRANSPORT: 'freight-transport',
};

export const intensityUnits = {
  FLOOR_METERS: 'm2',
  FLOOR_FEET: 'sqft',
  DISTANCE_KM: 'km',
  DISTANCE_MILES: 'miles',
  PASSENGER: 'passenger',
  VEHICLE: 'vehicle',
  AIRCRAFT: 'aircraft',
  SPENT: 'spent',
  REVENUE: 'revenue',
  TONNE_KM: 'tonne-km',
};

export const intensityUnitsFormatted = {
  FLOOR_METERS: 'tCO₂e/m²',
  FLOOR_FEET: 'tCO₂e/ft²',
  DISTANCE_KM: 'tCO₂e/km',
  DISTANCE_MILES: 'tCO₂e/mile',
  PASSENGER: 'tCO₂e/passenger',
  VEHICLE: 'tCO₂e/vehicle',
  AIRCRAFT: 'tCO₂e/aircraft',
  SPENT: 'tCO₂e/',
  REVENUE: 'tCO₂e/',
  TONNE_KM: 'tCO₂e/tonne-km',
};

export const questionSelectorOptions = {
  ACTIVITY: 'Activity data',
  CUSTOM: 'Custom data',
  SUPPLIER: 'Supplier data',
  SPEND: 'Spend data',
};
